import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAuditorInvoiceAi,
  fetchAuditorInvoiceAiStatus,
  saveAuditorInvoiceAiCorrections,
  saveAuditorInvoiceAiReviews,
  reanalyzeAuditorInvoiceAi,
  startAuditorInvoiceAi,
  type InvoiceAiActCorrection,
  type InvoiceAiActReview,
  type InvoiceAiAuditContext,
  type InvoiceAiAnalysisRow,
} from "../api/auditorApi";
import { sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import {
  analysisRowAmounts,
  formatManualMeta,
  inferExchangeRateFromTable,
  isManualActIndex,
  isSynthesisRow,
  MANUAL_ACT_INDEX_BASE,
  nextManualActIndex,
  parseManualMeta,
  roundMoney,
} from "../lib/invoiceAiRowAmounts";

const ANOMALY_TYPES = new Set(["surcharge", "non_tarifé", "sans_proforma", "sous_facturation"]);

function actIndex(row: InvoiceAiAnalysisRow, idx: number): number {
  return row.act_orig_index ?? idx;
}

function lineTotal(
  row: InvoiceAiAnalysisRow,
  currency: string,
  exchangeRateCdf: number,
): number {
  return analysisRowAmounts(row, currency, exchangeRateCdf).total;
}

function defaultReview(
  row: InvoiceAiAnalysisRow,
  idx: number,
  currency: string,
  exchangeRateCdf: number,
  existing?: InvoiceAiActReview,
): InvoiceAiActReview {
  const index = actIndex(row, idx);

  if (isSynthesisRow(row)) {
    return {
      act_index: index,
      review_status: "rejected",
      montant_facture: 0,
      montant_retenu: 0,
      review_note: existing?.review_note || "Ligne synthèse — non comptabilisée",
    };
  }

  const total = lineTotal(row, currency, exchangeRateCdf);
  const typeObs = row.type_obs ?? "ok";
  const autoApprove = !ANOMALY_TYPES.has(typeObs);

  if (existing && existing.review_status === "pending" && autoApprove) {
    // Ligne redevenue conforme (ex. après ré-analyse) : le serveur l'approuve d'office
    // (InvoiceActApprovalService::effectiveActReview) — on affiche la même chose.
    return {
      act_index: index,
      review_status: "approved",
      montant_facture: total,
      montant_retenu: total,
      review_note: existing.review_note ?? "",
    };
  }

  if (existing) {
    // MySQL DECIMAL arrive en chaîne ("40.00") : sans conversion, les totaux deviennent NaN.
    return {
      act_index: index,
      review_status: existing.review_status,
      montant_facture: existing.montant_facture != null ? Number(existing.montant_facture) : total,
      montant_retenu:
        existing.montant_retenu != null
          ? Number(existing.montant_retenu)
          : autoApprove
            ? total
            : undefined,
      approve_without_proforma:
        existing.approve_without_proforma != null ? Number(existing.approve_without_proforma) : undefined,
      review_note: existing.review_note ?? "",
    };
  }

  return {
    act_index: index,
    review_status: autoApprove ? "approved" : "pending",
    montant_facture: total,
    montant_retenu: autoApprove ? total : undefined,
    review_note: "",
  };
}

function defaultCorrection(
  row: InvoiceAiAnalysisRow,
  idx: number,
  currency: string,
  exchangeRateCdf: number,
  existing?: InvoiceAiActCorrection,
): InvoiceAiActCorrection {
  const index = actIndex(row, idx);
  const qty = Math.max(1, row.qty ?? 1);
  const amounts = analysisRowAmounts(row, currency, exchangeRateCdf);
  const unit = amounts.unit;
  const name = row.act_name ?? "";

  if (existing) {
    return {
      act_index: index,
      original_name: existing.original_name ?? (row.is_manual ? "" : name),
      corrected_name: existing.corrected_name ?? name,
      original_qty: existing.original_qty ?? (row.is_manual ? 0 : qty),
      corrected_qty: existing.corrected_qty ?? qty,
      original_price: existing.original_price ?? (row.is_manual ? 0 : unit),
      corrected_price: existing.corrected_price ?? unit,
      tariff_code: existing.tariff_code ?? "",
      note_correction: existing.note_correction ?? "",
    };
  }

  return {
    act_index: index,
    original_name: row.is_manual ? "" : name,
    corrected_name: name,
    original_qty: row.is_manual ? 0 : qty,
    corrected_qty: qty,
    original_price: row.is_manual ? 0 : unit,
    corrected_price: unit,
    tariff_code: "",
    note_correction: row.is_manual
      ? formatManualMeta(row.patient ?? "", row.date ?? "")
      : "",
  };
}

function manualRowFromCorrection(
  actIndex: number,
  corr: InvoiceAiActCorrection,
  currency: string,
  exchangeRateCdf: number,
): InvoiceAiAnalysisRow {
  const meta = parseManualMeta(corr.note_correction);
  const qty = Math.max(1, corr.corrected_qty ?? 1);
  const unit = corr.corrected_price ?? 0;
  const total = roundMoney(unit * qty, currency);
  const row: InvoiceAiAnalysisRow = {
    act_orig_index: actIndex,
    act_name: corr.corrected_name ?? "",
    patient: meta?.patient ?? "",
    date: meta?.date || undefined,
    qty,
    type_obs: "ok",
    is_manual: true,
  };
  if (currency === "CDF") {
    row.prix_facture_cdf = unit;
    row.total_facture_cdf = total;
  } else {
    row.prix_facture = unit;
    row.total_facture = total;
  }
  if (exchangeRateCdf > 0 && currency === "CDF") {
    row.prix_facture = roundMoney(unit / exchangeRateCdf, "USD");
    row.total_facture = roundMoney(total / exchangeRateCdf, "USD");
  }
  return row;
}

/** Délai d'inactivité avant enregistrement automatique des validations / corrections. */
const AUTOSAVE_DELAY_MS = 2000;

export type InvoiceAiSaveState = "idle" | "saving" | "saved" | "error";

export function useInvoiceAiAudit(
  invoiceId: number,
  enabled: boolean,
  options: { canReview?: boolean } = {},
) {
  const canReview = options.canReview ?? false;
  const [context, setContext] = useState<InvoiceAiAuditContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Record<number, InvoiceAiActReview>>({});
  const [corrections, setCorrections] = useState<Record<number, InvoiceAiActCorrection>>({});
  const [manualRows, setManualRows] = useState<InvoiceAiAnalysisRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editVersion, setEditVersion] = useState(0);
  const [saveState, setSaveState] = useState<InvoiceAiSaveState>("idle");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const editVersionRef = useRef(0);
  const inflightSaveRef = useRef<Promise<boolean> | null>(null);
  const reviewsRef = useRef(reviews);
  const correctionsRef = useRef(corrections);
  reviewsRef.current = reviews;
  correctionsRef.current = corrections;

  const markDirty = useCallback(() => {
    editVersionRef.current += 1;
    setEditVersion(editVersionRef.current);
    setDirty(true);
  }, []);

  const currency = context?.invoice_currency ?? "USD";
  const exchangeRateCdf = useMemo(() => {
    const fromApi = context?.exchange_rate_cdf ?? 0;
    if (fromApi > 0) return fromApi;
    return inferExchangeRateFromTable(context?.analysis_table ?? []);
  }, [context?.exchange_rate_cdf, context?.analysis_table]);

  const syncReviewsFromContext = useCallback(
    (ctx: InvoiceAiAuditContext, manual: InvoiceAiAnalysisRow[]) => {
      const cur = ctx.invoice_currency ?? "USD";
      const rate = ctx.exchange_rate_cdf ?? 0;
      const next: Record<number, InvoiceAiActReview> = {};
      const rows = [...(ctx.analysis_table ?? []), ...manual];
      rows.forEach((row, idx) => {
        const index = actIndex(row, idx);
        const existing = ctx.act_reviews[String(index)] ?? ctx.act_reviews[index];
        next[index] = defaultReview(row, idx, cur, rate, existing as InvoiceAiActReview | undefined);
      });
      setReviews(next);
    },
    [],
  );

  const syncCorrectionsFromContext = useCallback(
    (ctx: InvoiceAiAuditContext, manual: InvoiceAiAnalysisRow[]) => {
      const cur = ctx.invoice_currency ?? "USD";
      const rate = ctx.exchange_rate_cdf ?? 0;
      const next: Record<number, InvoiceAiActCorrection> = {};
      const rows = [...(ctx.analysis_table ?? []), ...manual];
      rows.forEach((row, idx) => {
        const index = actIndex(row, idx);
        const existing = ctx.act_corrections?.[String(index)] ?? ctx.act_corrections?.[index];
        next[index] = defaultCorrection(row, idx, cur, rate, existing as InvoiceAiActCorrection | undefined);
      });
      setCorrections(next);
    },
    [],
  );

  const extractManualRows = useCallback((ctx: InvoiceAiAuditContext): InvoiceAiAnalysisRow[] => {
    const cur = ctx.invoice_currency ?? "USD";
    const rate = ctx.exchange_rate_cdf ?? 0;
    const maxIdx = Math.max(
      0,
      ...(ctx.analysis_table ?? []).map((r, i) => actIndex(r, i)),
    );
    const corrs = ctx.act_corrections ?? {};
    const manual: InvoiceAiAnalysisRow[] = [];
    for (const key of Object.keys(corrs)) {
      const idx = Number(key);
      const corr = corrs[key];
      if (!corr) continue;
      const hasManualMeta = !!parseManualMeta(corr.note_correction);
      if (!isManualActIndex(idx, maxIdx) && !hasManualMeta) continue;
      manual.push(manualRowFromCorrection(idx, corr, cur, rate));
    }
    manual.sort((a, b) => (a.act_orig_index ?? 0) - (b.act_orig_index ?? 0));
    return manual;
  }, []);

  /** Remplace l'état local par celui du serveur (après chargement ou enregistrement). */
  const applyContext = useCallback(
    (ctx: InvoiceAiAuditContext) => {
      const manual = extractManualRows(ctx);
      setContext(ctx);
      setManualRows(manual);
      syncReviewsFromContext(ctx, manual);
      syncCorrectionsFromContext(ctx, manual);
      setDirty(false);
    },
    [extractManualRows, syncCorrectionsFromContext, syncReviewsFromContext],
  );

  const load = useCallback(async () => {
    if (!enabled || invoiceId <= 0) return;
    setLoading(true);
    setError(null);
    try {
      applyContext(await fetchAuditorInvoiceAi(invoiceId));
    } catch (err) {
      setError(sanitizeApiErrorMessage(err instanceof Error ? err.message : "Erreur IA."));
    } finally {
      setLoading(false);
    }
  }, [applyContext, enabled, invoiceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setAnalyzing(false);
  }, []);

  const pollStatus = useCallback(() => {
    stopPolling();
    setAnalyzing(true);
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const status = await fetchAuditorInvoiceAiStatus(invoiceId);
          if (status.status === "completed") {
            stopPolling();
            await load();
          } else if (status.status === "failed") {
            stopPolling();
            setError(status.error ?? status.message ?? "Analyse IA échouée.");
          }
        } catch {
          /* ignore polling errors */
        }
      })();
    }, 3000);
  }, [invoiceId, load, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const jobStatus = context?.job?.status;
  useEffect(() => {
    if (jobStatus === "queued" || jobStatus === "running") {
      pollStatus();
    }
  }, [jobStatus, pollStatus]);

  /** Ré-analyse complète (relecture PDF) : efface validations et corrections côté serveur. */
  const startAnalysis = useCallback(
    async (force = false) => {
      setError(null);
      try {
        await startAuditorInvoiceAi(invoiceId, force);
        setDirty(false);
        pollStatus();
      } catch (err) {
        setError(sanitizeApiErrorMessage(err instanceof Error ? err.message : "Lancement IA impossible."));
      }
    },
    [invoiceId, pollStatus],
  );

  /**
   * Enregistre validations ET corrections en une seule opération.
   * Si l'auditeur a modifié des lignes pendant l'envoi, l'état local est conservé
   * (pas de resynchronisation) et un nouvel enregistrement automatique suivra.
   */
  const saveAll = useCallback(async (): Promise<boolean> => {
    // File d'attente stricte : chaque appel s'enchaîne après le précédent (jamais deux en parallèle).
    const previous = inflightSaveRef.current;
    const run = (async () => {
      if (previous) {
        await previous.catch(() => false);
      }
      const version = editVersionRef.current;
      setSaving(true);
      setSaveState("saving");
      setError(null);
      try {
        if (canReview) {
          await saveAuditorInvoiceAiReviews(invoiceId, Object.values(reviewsRef.current));
        }
        const { context: ctx } = await saveAuditorInvoiceAiCorrections(
          invoiceId,
          Object.values(correctionsRef.current),
        );
        if (editVersionRef.current === version) {
          applyContext(ctx);
        } else {
          setContext(ctx);
        }
        setSaveState("saved");
        return true;
      } catch (err) {
        setError(sanitizeApiErrorMessage(err instanceof Error ? err.message : "Enregistrement impossible."));
        setSaveState("error");
        return false;
      } finally {
        setSaving(false);
      }
    })();
    inflightSaveRef.current = run;
    try {
      return await run;
    } finally {
      if (inflightSaveRef.current === run) inflightSaveRef.current = null;
    }
  }, [applyContext, canReview, invoiceId]);

  // Enregistrement automatique après chaque modification (auditeur uniquement).
  useEffect(() => {
    if (!enabled || !canReview || !dirty || editVersion === 0) return;
    const timer = window.setTimeout(() => {
      void saveAll();
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [canReview, dirty, editVersion, enabled, saveAll]);

  // Avertit le navigateur si l'onglet est fermé avec des modifications non enregistrées.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const updateReview = useCallback(
    (actIndex: number, patch: Partial<InvoiceAiActReview>) => {
      setReviews((prev) => ({
        ...prev,
        [actIndex]: { ...prev[actIndex], ...patch, act_index: actIndex },
      }));
      markDirty();
    },
    [markDirty],
  );

  const updateCorrection = useCallback(
    (actIndex: number, patch: Partial<InvoiceAiActCorrection>) => {
      setCorrections((prev) => ({
        ...prev,
        [actIndex]: { ...prev[actIndex], ...patch, act_index: actIndex },
      }));
      markDirty();
    },
    [markDirty],
  );

  const addManualRow = useCallback(
    (payload: { patient: string; date: string; actName: string; qty: number; unitPrice: number }) => {
      const indices = [
        ...(context?.analysis_table ?? []).map((r, i) => actIndex(r, i)),
        ...manualRows.map((r) => r.act_orig_index ?? MANUAL_ACT_INDEX_BASE),
      ];
      const newIndex = nextManualActIndex(indices);
      const qty = Math.max(1, payload.qty);
      const unit = roundMoney(payload.unitPrice, currency);
      const total = roundMoney(unit * qty, currency);
      const row: InvoiceAiAnalysisRow = {
        act_orig_index: newIndex,
        act_name: payload.actName.trim(),
        patient: payload.patient.trim(),
        date: payload.date || undefined,
        qty,
        type_obs: "ok",
        is_manual: true,
      };
      if (currency === "CDF") {
        row.prix_facture_cdf = unit;
        row.total_facture_cdf = total;
        if (exchangeRateCdf > 0) {
          row.prix_facture = roundMoney(unit / exchangeRateCdf, "USD");
          row.total_facture = roundMoney(total / exchangeRateCdf, "USD");
        }
      } else {
        row.prix_facture = unit;
        row.total_facture = total;
      }

      setManualRows((prev) => [...prev, row]);
      setReviews((prev) => ({
        ...prev,
        [newIndex]: {
          act_index: newIndex,
          review_status: "pending",
          montant_facture: total,
          montant_retenu: total,
          review_note: "",
        },
      }));
      setCorrections((prev) => ({
        ...prev,
        [newIndex]: {
          act_index: newIndex,
          original_name: "",
          corrected_name: row.act_name,
          original_qty: 0,
          corrected_qty: qty,
          original_price: 0,
          corrected_price: unit,
          tariff_code: "",
          note_correction: formatManualMeta(row.patient ?? "", row.date ?? ""),
        },
      }));
      markDirty();
    },
    [context?.analysis_table, currency, exchangeRateCdf, manualRows, markDirty],
  );

  const removeManualRow = useCallback(
    (actIndex: number) => {
      setManualRows((prev) => prev.filter((r) => (r.act_orig_index ?? -1) !== actIndex));
      setReviews((prev) => {
        const next = { ...prev };
        delete next[actIndex];
        return next;
      });
      setCorrections((prev) => {
        const next = { ...prev };
        delete next[actIndex];
        return next;
      });
      markDirty();
    },
    [markDirty],
  );

  const reanalyze = useCallback(async () => {
    setReanalyzing(true);
    setError(null);
    try {
      const result = await reanalyzeAuditorInvoiceAi(invoiceId);
      applyContext(result.context);
      return result;
    } catch (err) {
      setError(sanitizeApiErrorMessage(err instanceof Error ? err.message : "Ré-analyse impossible."));
      return null;
    } finally {
      setReanalyzing(false);
    }
  }, [applyContext, invoiceId]);

  /** Enregistre tout, puis recalcule les anomalies à partir des corrections (validations conservées). */
  const saveAndReanalyze = useCallback(async () => {
    const ok = await saveAll();
    if (!ok) return null;
    return reanalyze();
  }, [reanalyze, saveAll]);

  return {
    context,
    loading,
    analyzing,
    saving,
    reanalyzing,
    error,
    reviews,
    corrections,
    manualRows,
    dirty,
    saveState,
    load,
    startAnalysis,
    saveAll,
    saveAndReanalyze,
    reanalyze,
    updateReview,
    updateCorrection,
    addManualRow,
    removeManualRow,
  };
}
